import React, { useRef, useState } from 'react';
import { Upload, FileText, X, CheckCircle2, AlertCircle } from 'lucide-react';

interface FileUploadProps {
  label: string;
  hint?: string;
  accept?: string;
  maxSizeMB?: number;
  value?: File | null;
  onChange: (file: File | null) => void;
  required?: boolean;
  error?: string;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  label,
  hint,
  accept = '.pdf,.jpg,.jpeg,.png',
  maxSizeMB = 5,
  value,
  onChange,
  required,
  error: externalError,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [sizeError, setSizeError] = useState('');

  const error = externalError || sizeError;

  const handleFile = (file: File) => {
    setSizeError('');
    if (file.size > maxSizeMB * 1024 * 1024) {
      setSizeError(`File exceeds ${maxSizeMB} MB limit.`);
      return;
    }
    onChange(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSizeError('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
      <label className="lbl">
        {label}
        {required && <span style={{ color: 'var(--err)', marginLeft: '2px' }}>*</span>}
      </label>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        style={{ display: 'none' }}
      />

      {value ? (
        <div
          className="file-upload-zone has-file"
          style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'default' }}
        >
          <CheckCircle2 size={18} color="var(--succ)" style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 6, overflow: 'hidden' }}>
            <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              {value.name}
            </span>
            <span style={{ fontSize: '12px', color: 'var(--text-2)', flexShrink: 0 }}>
              {formatSize(value.size)}
            </span>
          </span>
          <button
            type="button"
            onClick={handleRemove}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', display: 'flex', padding: '2px' }}
            aria-label="Remove file"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div
          className={['file-upload-zone', dragOver ? 'drag-over' : '', error ? 'err-inp' : ''].filter(Boolean).join(' ')}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <Upload size={20} color="var(--text-3)" style={{ marginBottom: '6px' }} />
          <p style={{ margin: 0, fontSize: '13.5px', fontWeight: 600, color: 'var(--text-1)' }}>
            Drop file here or <span style={{ color: 'var(--p)' }}>browse</span>
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-2)' }}>
            {hint || `PDF, JPG or PNG · max ${maxSizeMB} MB`}
          </p>
        </div>
      )}

      {error && (
        <span className="err-msg" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <AlertCircle size={12} />
          {error}
        </span>
      )}
    </div>
  );
};

export { FileText as FileIcon };
